/**
 * Seat money that is part of a booking's financed total.
 *
 * Seats picked on the live map during checkout are folded into the held subtotal
 * before both fees (Review.jsx L479-484, cartPricing.js L135-144), so they ride
 * the installment plan and carry the service fee and hold fee on top. But no
 * breakdown surface rendered a seat row, and `baseFareDisplay` is back-derived as
 * `grandTotalWithHold − serviceFee − holdFee` — so the seat cost surfaced silently
 * inside "Base Fare". 13DYI1 showed Base Fare $1,778.00 against an airline fare of
 * $1,760.00, the $18.00 seat invisible.
 *
 * IMPORTANT — only `seat_selections_detail` counts. Seats bought AFTER booking go
 * through `/api/seat-purchase`, which charges its own Stripe PaymentIntent with a
 * flat 5% processing fee and writes `flight_data.seat_purchase`. That money never
 * enters the installment total, so folding it into this breakdown would invent a
 * charge the plan doesn't carry. Read `seat_purchase` separately if you need to
 * show post-booking seat spend.
 */

/** Duffel seat prices arrive as `{ amount: "18.00", currency }`; older/synthetic
 *  rows can carry a bare number. Mirrors Review.jsx's reducer exactly. */
function seatPriceToNumber(price) {
  if (price && typeof price === 'object' && 'amount' in price) {
    const n = parseFloat(price.amount);
    return Number.isFinite(n) ? n : 0;
  }
  const n = parseFloat(price ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Sum the listed price of every seat picked during checkout.
 *
 * @param {object|null} flightData a booking's `flight_data`
 * @returns {number} dollars (not cents); 0 when no seats were picked at checkout
 */
export function checkoutSeatTotalFromFlightData(flightData) {
  const bySegment = flightData?.seat_selections_detail;
  if (!bySegment || typeof bySegment !== 'object') return 0;

  let total = 0;
  for (const seats of Object.values(bySegment)) {
    if (!seats || typeof seats !== 'object') continue;
    for (const selection of Object.values(seats)) {
      total += seatPriceToNumber(selection?.price);
    }
  }
  // Guard against a corrupt row producing a negative line item.
  return total > 0 ? total : 0;
}

/**
 * How many seats were picked at checkout — including free ones, so the row can
 * read "Seat Selection (2)" while still pricing at $0.00. Mirrors Review.jsx's
 * `hasSelectedSeats`, which deliberately counts $0 picks too.
 */
export function checkoutSeatCountFromFlightData(flightData) {
  const bySegment = flightData?.seat_selections_detail;
  if (!bySegment || typeof bySegment !== 'object') return 0;
  return Object.values(bySegment).reduce(
    (n, seats) => n + (seats && typeof seats === 'object' ? Object.keys(seats).length : 0),
    0,
  );
}
